import { z, type ZodType } from 'zod';

function parseStoredJson<T>(raw: string, schema?: ZodType<T>): T | null {
  const parsed: unknown = JSON.parse(raw);
  if (schema) {
    const result = schema.safeParse(parsed);
    return result.success ? result.data : null;
  }
  return z.custom<T>((_value): _value is T => true).parse(parsed);
}

export const getItemFromLocalStorage = <T = unknown>(
  key: string,
  schema?: ZodType<T>
): T | null => {
  if (typeof window === 'undefined') {
    return null;
  }

  const item = window.localStorage.getItem(key);

  if (item == null) return null;

  try {
    return parseStoredJson(item, schema);
  } catch {
    return null;
  }
};

export const setItemInLocalStorage = (key: string, value: unknown): void => {
  if (typeof window === 'undefined') {
    return;
  }

  try {
    window.localStorage.setItem(key, JSON.stringify(value));
  } catch {
    // Ignore storage errors (e.g., private mode)
  }
};

export const removeItemFromLocalStorage = (key: string): void => {
  if (typeof window === 'undefined') {
    return;
  }

  try {
    window.localStorage.removeItem(key);
  } catch {
    // Ignore storage errors
  }
};

// Session storage helpers
export const getItemFromSessionStorage = <T = unknown>(
  key: string,
  schema?: ZodType<T>
): T | null => {
  if (typeof window === 'undefined') {
    return null;
  }

  try {
    const stored = sessionStorage.getItem(key);
    if (stored) {
      return parseStoredJson(stored, schema);
    }
  } catch {
    // Ignore storage errors (e.g., private mode, invalid JSON)
  }
  return null;
};

export const setItemInSessionStorage = (key: string, value: unknown): void => {
  if (typeof window === 'undefined') {
    return;
  }

  try {
    sessionStorage.setItem(key, JSON.stringify(value));
  } catch {
    // Ignore storage errors (e.g., private mode)
  }
};

export const removeItemFromSessionStorage = (key: string): void => {
  if (typeof window === 'undefined') {
    return;
  }

  try {
    sessionStorage.removeItem(key);
  } catch {
    // Ignore storage errors
  }
};
